import React, { FC, useState, useEffect } from "react";
import { Box, Button, Typography, TextField } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { TFunction } from "i18next";
import { useSelector, useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import CreateViewModal from "./viewsMenuSettings/CreateViewModal";
import SuccessMessage from "./viewsMenuSettings/SuccessMessage";
import SavedViewsList from "./viewsMenuSettings/SavedViewsList";
import DeleteViewModal from "./viewsMenuSettings/DeleteViewModal";
import { ViewsMenuSettingsStyles } from "./viewsMenuSettings/viewsMenuSettingsStyles";
import { normalizeText } from "../../../../../helpers/helpersSearchs";

import { resetSaveViewState } from "../../../../../../redux/slices/dataTable/dataViews";

import { dataTableViewsConfigurationState } from "../../../../../../redux/selectors/unifiedSelectors";

interface SavedView {
  id: string;
  name: string;
  savedDate: string;
  filters?: any;
  configuration?: any;
  dataViews?: any[];
  createdAt?: string;
}

type ViewsMenuParams = {
  t: TFunction<"global", undefined>;
  onClose?: () => void;
  setTableConfiguration: (config: any) => void;
  createNewView: () => Promise<any>;
  getViews: () => Promise<any>;
  //dataViews: any;
  resetTableView: () => void;
  setDeleteViewSuccessRested: () => void;
  deleteView: (viewId: string) => void;
  dataTableApplyView: (viewConfig: any) => void;
  saveCurrentView: (viewData: {
    viewName: string;
    viewDescription: string;
    tableConfig: any;
    filtersConfig: any;
    dataDateActivityConfig: any;
    hasDateFilterChanged: boolean;
  }) => Promise<any>;
};

const ViewsMenu: FC<ViewsMenuParams> = ({
  t,
  onClose,
  resetTableView,
  deleteView,
  dataTableApplyView,
  setDeleteViewSuccessRested,
  saveCurrentView,
}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const { tableConfig, filtersConfig, dataDateActivityConfig, dataView } =
    useSelector(dataTableViewsConfigurationState);

  const {
    dataViews,
    isCreatingView,
    saveViewSuccess,
    saveViewError,
    reduxSavedViews,
    deleteViewStatus,
  } = dataView;

  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [newViewDescription, setNewViewDescription] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [viewToDelete, setViewToDelete] = useState<SavedView | null>(null);
  const [saveFilters, setSaveFilters] = useState(true);

  useEffect(() => {
    if (dataViews?.data) {
      const dataViewsObject = dataViews.data.map((views: any) => ({
        id: views.id,
        name: views.name,
        description: views.description,
        savedDate: new Date(views.createdAt).toLocaleDateString(),
        filters: views.config.filters,
        configuration: views.config.table,
        createdAt: views.createdAt,
        dateFilter: views.config.date || null,
        version: views.config.version || "1",
      }));
      setSavedViews(dataViewsObject);
    }
  }, [dataViews, saveViewSuccess]);

  useEffect(() => {
    if (saveViewSuccess) {
      setSuccessMessage(t("Data_views.view_created"));
      setShowSuccessMessage(true);
      handleCloseModal();
      if (reduxSavedViews.length > 0) {
        setSavedViews(reduxSavedViews);
      }
      dispatch(resetSaveViewState());
    }
  }, [saveViewSuccess, reduxSavedViews, t, dispatch]);

  useEffect(() => {
    if (deleteViewStatus.success) {
      setSuccessMessage(t("Data_views.view_deleted"));
      setShowSuccessMessage(true);
      setDeleteViewSuccessRested();
      setDeleteModalOpen(false);
      setViewToDelete(null);
    }
  }, [deleteViewStatus.success, t, setDeleteViewSuccessRested]);

  useEffect(() => {
    if (saveViewError) {
      alert(t("Data_views.try_again"));

      dispatch(resetSaveViewState());
    }
  }, [saveViewError, t, dispatch]);

  const hasDateFilterChanged = () => {
    const currentDates = dataDateActivityConfig.dataDateActivity;

    const defaultStartDate = new Date(
      new Date().setDate(new Date().getDate() - 7),
    );
    const defaultEndDate = new Date(new Date().setDate(new Date().getDate()));

    const normalizeDate = (date: Date) => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };

    const currentStart = normalizeDate(new Date(currentDates[0]));
    const currentEnd = normalizeDate(new Date(currentDates[1]));
    const defaultStart = normalizeDate(defaultStartDate);
    const defaultEnd = normalizeDate(defaultEndDate);

    return (
      currentStart.getTime() !== defaultStart.getTime() ||
      currentEnd.getTime() !== defaultEnd.getTime()
    );
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewViewName("");
    setNewViewDescription("");
  };

  const handleSaveCurrentView = () => {
    if (!newViewName.trim()) {
      alert(t("Data_views.enter_name"));
      return;
    }

    saveCurrentView({
      viewName: newViewName,
      viewDescription: newViewDescription,
      tableConfig,
      filtersConfig: saveFilters ? filtersConfig.filterNewModel : [],
      dataDateActivityConfig,
      hasDateFilterChanged: hasDateFilterChanged(),
    });
  };

  const handleCloseSuccessMessage = () => {
    setShowSuccessMessage(false);
  };

  const handleDeleteView = (id: string) => {
    const view = savedViews.find((v) => v.id === id);
    if (view) {
      setViewToDelete(view);
      setDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = (viewId: string) => {
    deleteView(viewId);
  };

  const handleCloseDeleteModal = () => {
    if (!deleteViewStatus.loading) {
      setDeleteModalOpen(false);
      setViewToDelete(null);
    }
  };

  const handleLoadView = (view: SavedView) => {
    if (view.configuration) {
      dataTableApplyView(view);

      setSuccessMessage(`${view.name} !`);
      setShowSuccessMessage(true);

      if (onClose) {
        onClose();
      }
    }
  };

  const handleResetView = () => {
    resetTableView();
    if (onClose) {
      onClose();
    }
  };

  const filteredViews = savedViews.filter((view) => {
    if (searchTerm === "") {
      return true;
    }
    return normalizeText(view.name).includes(normalizeText(searchTerm));
  });

  const isLoading = isCreatingView || deleteViewStatus.loading;

  return (
    <Box sx={ViewsMenuSettingsStyles.menuContainer}>
      {/* Description */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={ViewsMenuSettingsStyles.menuDescription}
      >
        {t("Data_views.saved_views_desc")}
      </Typography>

      <div style={ViewsMenuSettingsStyles.containerButtons}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleOpenModal}
          disabled={isLoading}
          sx={ViewsMenuSettingsStyles.saveCurrentViewButton}
        >
          {t("Data_views.save_view")}
        </Button>

        {/* Reset View Button */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<RestartAltIcon />}
          onClick={handleResetView}
          disabled={isLoading}
          sx={ViewsMenuSettingsStyles.resetViewButton}
        >
          {t("Data_views.reset")}
        </Button>
      </div>

      {/* Search Field */}
      <TextField
        fullWidth
        size="small"
        placeholder={t("Data_views.search_views")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={isLoading}
        sx={ViewsMenuSettingsStyles.searchField}
      />

      {/* Saved Views List */}
      <SavedViewsList
        views={filteredViews}
        onViewClick={handleLoadView}
        onViewDelete={handleDeleteView}
        disabled={isLoading}
      />

      {/* Modal to create new view */}
      <CreateViewModal
        t={t}
        setSaveFilters={setSaveFilters}
        saveFilters={saveFilters}
        viewWithFilters={false}
        open={isModalOpen}
        viewName={newViewName}
        viewDescription={newViewDescription}
        onClose={handleCloseModal}
        onViewNameChange={setNewViewName}
        onViewDescriptionChange={setNewViewDescription}
        onSave={handleSaveCurrentView}
        isLoading={isCreatingView}
        existingViews={savedViews}
      />

      {/* success message */}
      <SuccessMessage
        open={showSuccessMessage}
        message={successMessage}
        onClose={handleCloseSuccessMessage}
      />

      {/* Delete View Modal */}
      <DeleteViewModal
        t={t}
        open={deleteModalOpen}
        viewName={viewToDelete?.name || ""}
        viewId={viewToDelete?.id || ""}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteViewStatus.loading}
      />
    </Box>
  );
};

export default ViewsMenu;
